import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Checkbox
} from "@libs/zapi-ui/shadcn";
import { AppButton, Typography } from "@libs/zapi-ui/custom";

type TExportOptions = {
  includeAnalysisQuestions: boolean;
  includeIntelligenceQuestions: boolean;
};

type TExportOptionsModalProps = {
  open: boolean;
  onClose: () => void;
  onExport: (options: TExportOptions) => void;
  docType: "pdf" | "docx" | "md";
  isExporting: boolean;
};

export function ExportOptionsModal({ open, onClose, onExport, docType, isExporting }: TExportOptionsModalProps) {
  const [includeAnalysisQuestions, setIncludeAnalysisQuestions] = useState(false);
  const [includeIntelligenceQuestions, setIncludeIntelligenceQuestions] = useState(false);

  const handleExport = () => {
    onExport({
      includeAnalysisQuestions,
      includeIntelligenceQuestions
    });
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
      setIncludeAnalysisQuestions(false);
      setIncludeIntelligenceQuestions(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            <Typography variant="heading4" color="zinc900">
              Export Options
            </Typography>
          </DialogTitle>
          <DialogDescription>
            <Typography variant="body2" color="zinc500">
              Select which sections to include in your {docType.toUpperCase()} export.
            </Typography>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="analysis-questions"
              checked={includeAnalysisQuestions}
              onCheckedChange={(checked) => setIncludeAnalysisQuestions(checked === true)}
              disabled={isExporting}
            />
            <label
              htmlFor="analysis-questions"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              <Typography variant="body2" color="zinc700">
                Include Analysis Questions
              </Typography>
            </label>
          </div>

          <div className="flex items-center space-x-3">
            <Checkbox
              id="intelligence-questions"
              checked={includeIntelligenceQuestions}
              onCheckedChange={(checked) => setIncludeIntelligenceQuestions(checked === true)}
              disabled={isExporting}
            />
            <label
              htmlFor="intelligence-questions"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              <Typography variant="body2" color="zinc700">
                Include Intelligence Questions
              </Typography>
            </label>
          </div>
        </div>

        <DialogFooter>
          <AppButton appearance="text" color="zinc100" onClick={handleClose} disabled={isExporting}>
            <Typography variant="label2" color="zinc700">
              Cancel
            </Typography>
          </AppButton>
          <AppButton appearance="contained" color="zinc950" onClick={handleExport} disabled={isExporting}>
            <Typography variant="label2" color={isExporting ? "zinc400" : "zinc100"}>
              {isExporting ? "Exporting..." : "Export"}
            </Typography>
          </AppButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
